import React from "react";
import {
  MoreHorizontal,
  Download,
  Upload,
  Pencil,
  Ban,
  Trash2,
  Repeat,
  FileText,
  FileSignature,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BankAccountWithMandatesDto,
  MandateSummaryDto,
  MandateDocumentVersionDto,
  MandateStatusEnum,
  MandateSequenceTypeEnum,
} from "../../../../stores/Sepa/sepa.types";
import { formatIbanForDisplay } from "../../../../stores/Sepa/sepa.validation";

const STATUS_LABEL: Record<string, string> = {
  [MandateStatusEnum.Draft]: "Entwurf",
  [MandateStatusEnum.Active]: "Aktiv",
  [MandateStatusEnum.Used]: "Verwendet",
  [MandateStatusEnum.Revoked]: "Widerrufen",
  [MandateStatusEnum.Expired]: "Abgelaufen",
};

const STATUS_CLASS: Record<string, string> = {
  [MandateStatusEnum.Active]: "border-emerald-300 bg-emerald-50 text-emerald-700",
  [MandateStatusEnum.Draft]: "border-slate-300 bg-slate-50 text-slate-600",
  [MandateStatusEnum.Used]: "border-slate-300 bg-slate-50 text-slate-500",
  [MandateStatusEnum.Revoked]: "border-rose-300 bg-rose-50 text-rose-700",
  [MandateStatusEnum.Expired]: "border-amber-300 bg-amber-50 text-amber-700",
};

interface Props {
  account: BankAccountWithMandatesDto;
  /** The mandate for this row, or null for a bank account that has none yet. */
  mandate: MandateSummaryDto | null;
  /** Creditor name fallback (from the accounts list) until the enriched DTO is regenerated. */
  accountName?: string;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  busy?: boolean;
  onDownloadForm: (mandate: MandateSummaryDto) => void;
  onDownloadVersion: (mandate: MandateSummaryDto, version: MandateDocumentVersionDto) => void;
  onActivate: (mandate: MandateSummaryDto, account: BankAccountWithMandatesDto) => void;
  onEdit: (mandate: MandateSummaryDto, account: BankAccountWithMandatesDto) => void;
  onRevoke: (mandate: MandateSummaryDto) => void;
  onDeleteMandate: (mandate: MandateSummaryDto) => void;
  onDeleteBank: (account: BankAccountWithMandatesDto) => void;
}

const DASH = <span className="text-slate-300">—</span>;

const fmtDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString("de-DE") : "—";

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col">
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
    <dd className="text-sm text-slate-700">{value}</dd>
  </div>
);

/** One table row (Kontoinhaber | IBAN | Art | Status | Mandant | Aktionen), expandable to details. */
const SepaMandateRow: React.FC<Props> = ({
  account,
  mandate,
  accountName,
  index,
  expanded,
  onToggle,
  busy,
  onDownloadForm,
  onDownloadVersion,
  onActivate,
  onEdit,
  onRevoke,
  onDeleteMandate,
  onDeleteBank,
}) => {
  const isRecurring = mandate?.sequenceType === MandateSequenceTypeEnum.Rcur;
  const canRevoke =
    !!mandate &&
    (mandate.status === MandateStatusEnum.Active || mandate.status === MandateStatusEnum.Draft);
  const isDraft = mandate?.status === MandateStatusEnum.Draft;
  // A signed document can be (re)uploaded while the mandate is live; every upload
  // is appended as a new immutable version, so a wrong/illegible scan can be replaced.
  const canUploadSigned =
    !!mandate &&
    (mandate.status === MandateStatusEnum.Draft ||
      mandate.status === MandateStatusEnum.Active ||
      mandate.status === MandateStatusEnum.Used);
  const TypeIcon = isRecurring ? Repeat : FileText;
  const mandantName = mandate?.accountName ?? accountName;
  const bic = (account.bic as unknown as string) || "—";
  const zebra = index % 2 === 1 ? "bg-slate-100" : "bg-white";

  return (
    <>
      <TableRow
        className={`${expanded ? "bg-sky-100 hover:bg-sky-100" : `${zebra} hover:bg-slate-100/70`} ${
          mandate ? "cursor-pointer" : ""
        }`}
        onClick={mandate ? onToggle : undefined}
      >
        <TableCell className="w-8 py-0 pl-4 pr-0">
          {mandate ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )
          ) : null}
        </TableCell>

        <TableCell className="py-0 text-center text-[15px] font-medium text-slate-800">
          {account.holderFirstName} {account.holderLastName}
        </TableCell>

        <TableCell className="whitespace-nowrap py-0 text-center font-mono text-sm text-slate-600">
          {formatIbanForDisplay(account.iban)}
        </TableCell>

        <TableCell className="py-0 text-center text-[15px]">
          {mandate ? (
            <span className="inline-flex items-center gap-1.5 text-slate-700">
              <TypeIcon className="h-4 w-4 text-slate-400" />
              {isRecurring ? "Mehrfach" : "Einmal"}
            </span>
          ) : (
            DASH
          )}
        </TableCell>

        <TableCell className="py-0 text-center">
          {mandate ? (
            <Badge
              variant="outline"
              className={`text-sm font-semibold ${STATUS_CLASS[mandate.status] ?? ""}`}
            >
              {STATUS_LABEL[mandate.status] ?? mandate.status}
            </Badge>
          ) : (
            DASH
          )}
        </TableCell>

        <TableCell className="py-0 text-center text-[15px] text-slate-700">
          {mandate ? (mandantName ?? DASH) : DASH}
        </TableCell>

        <TableCell className="py-0 text-center" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-500 hover:bg-[#4189a8] hover:text-white"
                disabled={busy}
                aria-label="Aktionen"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {mandate && (
                <DropdownMenuItem
                  className="focus:bg-[#4189a8] focus:text-white"
                  onClick={() => onDownloadForm(mandate)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Formular herunterladen
                </DropdownMenuItem>
              )}
              {mandate && mandate.status === MandateStatusEnum.Draft && (
                <DropdownMenuItem
                  className="focus:bg-[#4189a8] focus:text-white"
                  onClick={() => onEdit(mandate, account)}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Bearbeiten
                </DropdownMenuItem>
              )}
              {mandate && canUploadSigned && (
                <DropdownMenuItem
                  className="focus:bg-[#4189a8] focus:text-white"
                  onClick={() => onActivate(mandate, account)}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {isDraft ? "Signiert hochladen & bestätigen" : "Neue signierte Version hochladen"}
                </DropdownMenuItem>
              )}
              {mandate && canRevoke && (
                <DropdownMenuItem
                  className="focus:bg-[#4189a8] focus:text-white"
                  onClick={() => onRevoke(mandate)}
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Widerrufen
                </DropdownMenuItem>
              )}
              {mandate && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDeleteMandate(mandate)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Mandat löschen
                </DropdownMenuItem>
              )}
              {mandate && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDeleteBank(account)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Bankverbindung entfernen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      {expanded && mandate && (
        <TableRow className="bg-sky-100/60 hover:bg-sky-100/60">
          <TableCell colSpan={7} className="py-2">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 px-6 sm:grid-cols-3 lg:grid-cols-5">
              <DetailField
                label="Mandatsreferenz"
                value={
                  <span className="font-mono text-sm">
                    {mandate.mandateReference ?? mandate.id}
                  </span>
                }
              />
              <DetailField label="BIC" value={bic} />
              <DetailField label="Erstellt am" value={fmtDate(mandate.createdAt)} />
              <DetailField label="Unterschrieben am" value={fmtDate(mandate.signedDate)} />
              {!isRecurring && (
                <DetailField
                  label="Rezept"
                  value={
                    mandate.rxNumber ? (
                      <span className="font-mono text-sm">{mandate.rxNumber}</span>
                    ) : (
                      DASH
                    )
                  }
                />
              )}
              {mandate.revokedAt && (
                <DetailField label="Widerrufen am" value={fmtDate(mandate.revokedAt)} />
              )}
              {mandate.revokedAt && mandate.revocationReason && (
                <DetailField label="Widerrufsgrund" value={mandate.revocationReason} />
              )}
            </dl>

            <div className="mt-3 px-6">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <FileSignature className="h-3.5 w-3.5" />
                Unterschriebene Mandatsdokumente
              </div>
              {mandate.documentVersions && mandate.documentVersions.length > 0 ? (
                <ul className="divide-y divide-sky-200/60 overflow-hidden rounded-md border border-sky-200/60">
                  {mandate.documentVersions.map((version) => {
                    const isCurrent = version.version === mandate.documentVersion;
                    return (
                      <li
                        key={version.version}
                        className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <FileText className="h-5 w-5 shrink-0 text-[#4189a8]" />
                          <span className="flex min-w-0 flex-col">
                            <span className="flex items-center gap-2 font-medium text-slate-800">
                              Mandatsdokument · Version {version.version}
                              {isCurrent && (
                                <Badge
                                  variant="outline"
                                  className="border-emerald-300 bg-emerald-50 text-[10px] font-semibold text-emerald-700"
                                >
                                  Aktuell
                                </Badge>
                              )}
                            </span>
                            <span className="text-xs text-slate-500">
                              Unterschrieben am {fmtDate(version.signatureDate)} · bestätigt am{" "}
                              {fmtDate(version.confirmedAt)}
                              {version.confirmedByName ? ` von ${version.confirmedByName}` : ""}
                            </span>
                          </span>
                        </span>
                        {version.documentId && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 shrink-0 gap-1 border-slate-300 px-2 text-xs text-slate-600 hover:bg-[#4189a8] hover:text-white"
                            onClick={() => onDownloadVersion(mandate, version)}
                          >
                            <Download className="h-3 w-3" />
                            Herunterladen
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="rounded-md border border-dashed border-sky-200/60 px-3 py-2 text-sm text-slate-400">
                  Noch kein unterschriebenes Dokument hochgeladen.
                </p>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default SepaMandateRow;
